// Per-nutrient gauge configuration. Derives display range, target band, and
// status from the existing agronomy tables — no new agronomy logic. The
// results UI uses this to render horizontal bar gauges with a green target
// band and a status-tinted indicator.
//
// Pure functions; can be safely imported from client components.

import { GRASS_AGRONOMY, ORGANIC_MATTER_LOW_THRESHOLD_PCT, ORGANIC_MATTER_VERY_LOW_THRESHOLD_PCT } from './agronomy-tables'
import type { GrassKey, SoilTestValues } from './types'

export type GaugeStatus = 'ok' | 'low' | 'far_low' | 'high' | 'info'

export interface GaugeConfig {
  /** Display name on the gauge label. */
  name: string
  /** Numeric value to plot; null hides the gauge. */
  value: number | null
  /** Unit string shown after the value. */
  unit: string
  /** Left edge of the visible scale. */
  min: number
  /** Right edge of the visible scale. */
  max: number
  /** Left edge of the green target band. */
  targetMin: number
  /** Right edge of the green target band. */
  targetMax: number
  /** Drives indicator color + status pill. */
  status: GaugeStatus
  /** Short caption shown next to the name, e.g. "slightly acidic". */
  caption?: string
}

// ---------------------------------------------------------------------------
// Status classifier
// ---------------------------------------------------------------------------

function classify(value: number, targetMin: number, targetMax: number): GaugeStatus {
  if (value >= targetMin && value <= targetMax) return 'ok'
  if (value > targetMax) return 'high'
  if (value < targetMin * 0.5) return 'far_low'
  return 'low'
}

// ---------------------------------------------------------------------------
// Per-nutrient builders
// ---------------------------------------------------------------------------

function buildPhGauge(value: number | null, grass: GrassKey): GaugeConfig {
  const targets = GRASS_AGRONOMY[grass].ph
  const v = value
  const status: GaugeStatus = v === null ? 'info' : classify(v, targets.min, targets.max)
  const caption =
    v === null ? undefined
      : v < 5.5 ? 'strongly acidic'
      : v < targets.min ? 'slightly acidic'
      : v > targets.max ? 'alkaline'
      : 'in range'
  return {
    name: 'pH',
    value: v,
    unit: '',
    min: 4.0,
    max: 9.0,
    targetMin: targets.min,
    targetMax: targets.max,
    status,
    caption,
  }
}

function buildKGauge(value: number | null, grass: GrassKey): GaugeConfig | null {
  const t = GRASS_AGRONOMY[grass].potassium
  if (t === null) return null
  const v = value
  const status: GaugeStatus = v === null ? 'info' : classify(v, t.lowPpm, t.sufficientPpm)
  const max = Math.max(t.sufficientPpm * 1.5, 300)
  return {
    name: 'Potassium (K)',
    value: v,
    unit: 'ppm',
    min: 0,
    max,
    targetMin: t.lowPpm,
    targetMax: t.sufficientPpm,
    status,
    caption: v !== null && v < t.lowPpm ? `below ${t.lowPpm} ppm target` : undefined,
  }
}

function buildPGauge(value: number | null, grass: GrassKey): GaugeConfig | null {
  const t = GRASS_AGRONOMY[grass].phosphorus
  if (t === null) return null
  const v = value
  const status: GaugeStatus = v === null ? 'info' : classify(v, t.lowPpm, t.sufficientPpm)
  const max = Math.max(t.sufficientPpm * 1.5, 80)
  return {
    name: 'Phosphorus (P)',
    value: v,
    unit: 'ppm',
    min: 0,
    max,
    targetMin: t.lowPpm,
    targetMax: t.sufficientPpm,
    status,
    caption: v !== null && v < t.lowPpm ? `below ${t.lowPpm} ppm target` : undefined,
  }
}

// Ca / Mg aren't on a strict per-grass target in our agronomy tables — they're
// context-only. Use general turf ranges so the gauge still shows where the
// value sits.
function buildCaGauge(value: number | null): GaugeConfig {
  const v = value
  const targetMin = 400
  const targetMax = 2000
  const status: GaugeStatus = v === null ? 'info' : classify(v, targetMin, targetMax)
  return {
    name: 'Calcium (Ca)',
    value: v,
    unit: 'ppm',
    min: 0,
    max: 3000,
    targetMin,
    targetMax,
    status,
    caption: v !== null && v >= targetMin && v <= targetMax ? 'adequate' : undefined,
  }
}

function buildMgGauge(value: number | null): GaugeConfig {
  const v = value
  const targetMin = 60
  const targetMax = 240
  const status: GaugeStatus = v === null ? 'info' : classify(v, targetMin, targetMax)
  return {
    name: 'Magnesium (Mg)',
    value: v,
    unit: 'ppm',
    min: 0,
    max: 400,
    targetMin,
    targetMax,
    status,
    caption: v !== null && v >= targetMin && v <= targetMax ? 'adequate' : undefined,
  }
}

function buildOmGauge(value: number | null): GaugeConfig {
  const v = value
  const status: GaugeStatus = v === null ? 'info'
    : v < ORGANIC_MATTER_VERY_LOW_THRESHOLD_PCT ? 'far_low'
    : v < ORGANIC_MATTER_LOW_THRESHOLD_PCT ? 'low'
    : 'ok'
  return {
    name: 'Organic matter',
    value: v,
    unit: '%',
    min: 0,
    max: 10,
    targetMin: ORGANIC_MATTER_LOW_THRESHOLD_PCT,
    targetMax: 8,
    status,
    caption: v !== null && v < ORGANIC_MATTER_LOW_THRESHOLD_PCT ? 'below 3% target' : undefined,
  }
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Build the ordered list of gauges to render. Skips any nutrient the report
 * didn't extract (value === null) so the UI doesn't show empty bars.
 */
export function buildGaugeConfigs(values: SoilTestValues, grass: GrassKey): GaugeConfig[] {
  const candidates: Array<GaugeConfig | null> = [
    buildPhGauge(values.ph, grass),
    buildOmGauge(values.organicMatterPct),
    buildPGauge(values.phosphorusPpm, grass),
    buildKGauge(values.potassiumPpm, grass),
    buildCaGauge(values.calciumPpm),
    buildMgGauge(values.magnesiumPpm),
  ]
  return candidates.filter((g): g is GaugeConfig => g !== null && g.value !== null)
}
